import React, { useContext, useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { FeatureFlags, WorkflowType } from '@fabric-ui/components';
import { noop, QueryKey } from '@fabric-ui/utils';
import { SxProps, useTheme } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getFileFormat, getNetworkErrorCode, isNullOrUndefined } from '@utils/utils';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useThemeMode } from '../../context/AppThemeProvider';
import { useAvContext } from '../../context/AvContextProvider';
import { NotificationContext, WorkflowStatus } from '../../context/AvSnackBarProvider';
import { type UseQueryObjectProps } from '../../hooks/queryObjectAsync';
import { useQueryObjectAsyncWithCachedResult } from '../../hooks/queryObjectAsync/useQueryObjectAsyncWithCachedResults';
import useHandleError from '../../hooks/useHandleError';
import { PresignedUrlType, usePresignedUrl } from '../../hooks/usePresignedURL';
import useQueryObject from '../../hooks/useQueryObjectSql';
import SourceIconsFormatter from './components/LabelFormatters/SourceIconsFormatter';
import { getWidgetTypeConfig } from './constants';
import { defaultDeliveryConfig, isHistoricalQuery, removeTempIdsFromDashboard } from './CustomDashboardsUtils';
import { useDashboard } from './hooks/useDashboard';
import { patchHistoricalQueryTimeRange } from './patchHistoricalQueryTimeRange';
import {
  AllTypeWidgets,
  CustomDashboardDto,
  DashboardExportDto,
  DashboardReportPermission,
  DwQueryRequest,
  PermissionType,
  PivotTableTypeWidget,
  ReportsTypes,
  SavedAllTypeWidgets,
  SavedPivotTable,
  WidgetCategory,
} from './types';
import { AxisTickType, LabelFormatterOption } from './types/types';

/**
 *
 * @param params UseQueryObjectProps
 * @description Uses useQueryObjectAsync when a query is is historical - otherwise uses useQueryObject
 *
 * Always uses useQueryObject when historical-async FF is turned off
 */
export const useQueryObjectWithHistoricalAsync = ({ queryObject, ...params }: UseQueryObjectProps) => {
  const patchedQueryObject = (Array.isArray(queryObject) ? queryObject : [queryObject]).map(q => ({
    ...q,
    timeRange: patchHistoricalQueryTimeRange(q.timeRange),
  }));

  const isHistorical = Array.isArray(patchedQueryObject)
    ? patchedQueryObject.some(isHistoricalQuery)
    : isHistoricalQuery(patchedQueryObject);

  const isAsyncQuery = Boolean(isHistorical);
  const syncResults = useQueryObject({
    ...params,
    queryObject: patchedQueryObject,
    options: { ...params.options, enabled: !isAsyncQuery && params.options?.enabled },
  });

  const asyncResultsWithCachedResponse = useQueryObjectAsyncWithCachedResult({
    ...params,
    queryObject: patchedQueryObject,
    options: { ...params.options, enabled: isAsyncQuery && params.options?.enabled },
    showStaleData: true,
  });

  const { asyncQueryStatus } = asyncResultsWithCachedResponse;
  const responseCalculatedAt = asyncResultsWithCachedResponse?.responseCalculatedAt;
  return {
    ...(isAsyncQuery ? asyncResultsWithCachedResponse : syncResults),
    isAsyncQuery,
    responseCalculatedAt,
    asyncQueryStatus: isAsyncQuery ? asyncQueryStatus : undefined,
  };
};

export const useDashboardsList = (type: ReportsTypes, options?: { excludePredefined: boolean }) =>
  useGqlQuery<CustomDashboardDto[]>({
    queryKey: [QueryKey.CustomDashboardsList, type, options],
    query: GET_CUSTOM_DASHBOARDS,
    variables: { type, excludePredefined: options?.excludePredefined ?? false },
  });

export const useEditDashboardDetails = ({ id, onSuccess }) => {
  const { mutate: save } = useEditDashboards({ onSuccess });
  const { data: initDashboard } = useDashboard({
    id,
  });
  return dashboard => save({ customDashboard: { ...initDashboard, ...dashboard }, isCreate: false });
};

const normalizeSortingPath = ({
  sorting = [],
  supportsSortingPath,
}: {
  sorting?: DwQueryRequest['sorting'];
  supportsSortingPath: boolean;
}) =>
  sorting.map(({ path, ...sort }) => {
    if (!supportsSortingPath || isNullOrUndefined(path)) {
      return sort;
    }

    return { ...sort, path };
  });

const mapRequestForSave = (request, supportsSortingPath: boolean) => ({
  ...request,
  sorting: normalizeSortingPath({ sorting: request.sorting, supportsSortingPath }),
  ...(request.top ? { top: { ...request.top, offset: 0 } } : undefined),
});

export function mapWidgetsDrillDown(widgets: AllTypeWidgets[]) {
  return widgets.map(({ drillDownHierarchy, requests, ...widget }) => {
    const { supportsSortingPath = false } = getWidgetTypeConfig(widget.category, widget.type);

    return {
      ...widget,
      drillDownHierarchy: { fields: drillDownHierarchy?.fields },
      requests: widget.category === WidgetCategory.Text ? [] : requests.map(request => mapRequestForSave(request, supportsSortingPath)),
    };
  });
}

export const useEditDashboards = ({
  onSuccess,
  parseData,
}: {
  onSuccess?: (val: boolean, id: string, shouldGoBack: boolean, isCreate: boolean) => void;
  parseData?: (widget: AllTypeWidgets) => Exclude<SavedAllTypeWidgets, PivotTableTypeWidget> | SavedPivotTable;
}) => {
  const { api, featureFlags } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: reRun } = useReRunDashboard(noop);
  const handleError = useHandleError(featureFlags[FeatureFlags.ShowServerErrorInToast]);

  return useMutation({
    mutationFn: ({
      isRerun = false,
      isSaveAsNew = false,
      isCreate = false,
      shouldGoBack = true,
      hasNewName = false,
      customDashboard,
    }: {
      isRerun?: boolean;
      isSaveAsNew?: boolean;
      isCreate?: boolean;
      shouldGoBack?: boolean;
      hasNewName?: boolean;
      customDashboard: CustomDashboardDto;
    }) => {
      const {
        isIntegrationActive,
        predefined,
        sharedUsers,
        createdByUserId,
        updatedByUserId,
        updatedAt,
        lastRunAt,
        widgets,
        omitAliasPrefix,
        ...newCustomDashboard
      } = {
        ...customDashboard,
        id: isSaveAsNew ? undefined : customDashboard.id,
        name: isSaveAsNew && !hasNewName ? `${customDashboard.name} (new)` : customDashboard.name,
      };
      const newWidgets: any = mapWidgetsDrillDown(widgets);
      return api(!isCreate && !isSaveAsNew ? UPDATE_DASHBOARD : CREATE_DASHBOARD, {
        options: {
          dashboardReportDto: {
            ...removeTempIdsFromDashboard({
              ...newCustomDashboard,
              widgets: parseData ? newWidgets.map(widget => parseData(widget)) : newWidgets,
              ...(newCustomDashboard.deliveryConfig
                ? {}
                : {
                    deliveryConfig: defaultDeliveryConfig(getFileFormat(newCustomDashboard.type)),
                  }),
            }),
            omitAliasPrefix: Boolean(omitAliasPrefix),
          },
        },
        onError: err => onErrorSnackBar(enqueueSnackbar, err),
      })
        .then(async data => {
          const id = data?.data?.[!isCreate && !isSaveAsNew ? 'updateUserDashboardReport' : 'createUserDashboardReport']?.id;
          if (isRerun) {
            await reRun(id);
          }
          return id;
        })
        .then(id => onSuccess?.(isRerun, id, shouldGoBack, isCreate))
        .catch(e => {
          if (getNetworkErrorCode(e) === 400) {
            console.warn(e);
            enqueueSnackbar(e.message, { variant: 'error' });
          } else {
            handleError(e);
          }
        });
    },
  });
};

export const useEditSharedDashboardUsers = ({ onSuccess = d => d }: { onSuccess?: (data) => void }) => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      id,
      sharedUsers = {
        editors: [],
        viewers: [],
        type: PermissionType.private,
      },
    }: {
      id: string;
      sharedUsers: DashboardReportPermission;
    }) =>
      api(UPDATE_DASHBOARD_SHARED_USERS, {
        options: { sharedUsers, id },
        onError: err => onErrorSnackBar(enqueueSnackbar, err),
        onSuccess: data => onSuccess(data?.data?.updateUserDashboardReportSharedUsers),
      }),
  });
};

export const useReRunDashboard = onSuccess => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => api(RERUN_DASHBOARD, { options: { id }, onError: err => onErrorSnackBar(enqueueSnackbar, err), onSuccess }),
  });
};

export const useDeleteDashboards = onSuccess => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (ids: string[]) =>
      api(DELETE_DASHBOARDS, { options: { ids }, onError: err => onErrorSnackBar(enqueueSnackbar, err), onSuccess }),
  });
};

const onErrorSnackBar = (enqueueSnackbar, err) => enqueueSnackbar(`${err.message}`, { variant: 'error' });

export const useDuplicateDashboard = (onSuccess: (id: string, name?: string) => void) => {
  const { api } = useAvContext();
  return useMutation({
    mutationFn: (id: string) =>
      api(DUPLICATE_DASHBOARD, { options: { id } }) as Promise<{ data: { duplicateDashboardReport: { id: string; name?: string } } }>,
    onSuccess: data => onSuccess(data?.data?.duplicateDashboardReport?.id, data?.data?.duplicateDashboardReport?.name),
  });
};

export const useEditDashboardSchedule = (onSuccess: () => void) => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (dashboardScheduleDto: any) => api(EDIT_REPORT_SCHEDULE, { options: { dashboardScheduleDto } }),
    onSuccess: () => {
      enqueueSnackbar('Successfully saved scheduling', {
        variant: 'success',
      });
      onSuccess();
    },
    onError: err => onErrorSnackBar(enqueueSnackbar, err),
  });
};

export function useExportDashboard({
  dashboardExportDto,
  name,
  target,
}: {
  dashboardExportDto: DashboardExportDto;
  name?: string;
  target?: string;
}) {
  const { api } = useAvContext();
  const { mutate: getDashboardPresignedUrl } = usePresignedUrl(PresignedUrlType.QUERY, name, target);
  const { setRunInfo } = useContext(NotificationContext);
  const { mode } = useThemeMode();

  const mutation = useMutation({
    mutationFn: () =>
      api(EXPORT_PAGE, {
        options: {
          dashboardExportDto: { ...dashboardExportDto, themeMode: mode },
        },
      }),
    onSuccess: result =>
      new Promise((resolve, reject) => {
        // @ts-expect-error: untyped
        if (typeof result.data !== 'object' || !result.data || !result.data?.triggerDashboardExport) {
          reject(new Error('Invalid response from export dashboard API'));
          return;
        }

        setRunInfo({
          // @ts-expect-error: untyped
          ...result.data.triggerDashboardExport,
          returnStatusOnly: false,
          workflowType: WorkflowType.ExportDashboard,
          onSuccess: res => {
            if (res.status === WorkflowStatus.COMPLETED) {
              // @ts-expect-error: untyped
              getDashboardPresignedUrl({ wfId: result.data.triggerDashboardExport.wfId, runId: result.data.triggerDashboardExport.runId });
              resolve(res);
              return;
            }
            if (res.status === WorkflowStatus.FAILED) {
              reject(res);
            }
          },
        });
      }),
  });

  return { exportDashboard: mutation.mutate, isLoading: mutation.isPending };
}

export const useActivateDashboards = onSuccess => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ ids, active }: { ids: string[]; active: boolean }) =>
      api(ACTIVATE_REPORTS, {
        options: {
          ids,
          active,
        },
        onError: err => onErrorSnackBar(enqueueSnackbar, err),
      }),
    onSuccess,
  });
};
export const useAddAppDashboards = onSuccess => {
  const { api } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ ids, app }: { ids: string[]; app: string }) =>
      api(ADD_APP_REPORTS, {
        options: {
          ids,
          app,
        },
        onError: err => onErrorSnackBar(enqueueSnackbar, err),
      }),
    onSuccess,
  });
};

export const getCustomTickFormatter = ({
  labelFormattingOption,
  isVertical = true,
  rotation,
  sx,
  itemMaxWidth,
}: {
  labelFormattingOption?: LabelFormatterOption;
  isVertical?: boolean;
  rotation?: AxisTickType;
  sx?: SxProps;
  itemMaxWidth?: number;
}) => {
  if (!labelFormattingOption) {
    return undefined;
  }
  const formattingMap: Record<LabelFormatterOption, ((value) => React.JSX.Element) | undefined> = {
    [LabelFormatterOption.SOURCE_ICONS]: value => (
      <SourceIconsFormatter
        value={value.toString()}
        sx={sx}
        isVertical={isVertical}
        isXRotated={rotation === AxisTickType.Slanted}
        showText={false}
        itemMaxWidth={itemMaxWidth}
      />
    ),
    [LabelFormatterOption.SOURCE_ICONS_WITH_NAMES]: value => (
      <SourceIconsFormatter
        value={value.toString()}
        sx={sx}
        rotateText90deg={rotation === AxisTickType.Vertical}
        isVertical={isVertical}
        isXRotated={rotation === AxisTickType.Slanted}
        itemMaxWidth={itemMaxWidth}
      />
    ),
    [LabelFormatterOption.NONE]: undefined,
  };

  return formattingMap[labelFormattingOption];
};

const getCustomPalette = (colors: string[], dataLength: number = 0): string[] =>
  dataLength < colors.length ? colors : [...colors, ...getCustomPalette(colors, dataLength - colors.length)];

export const useCustomPalette = data => {
  const {
    palette: { chartColors: defaultColors },
  } = useTheme();

  return useMemo(() => getCustomPalette(defaultColors, data?.length), [data?.length]);
};

export const useSharableReportUsers = (enabled = true) => {
  const { api } = useAvContext();

  return useQuery({
    queryKey: ['getShareableUsers'],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(GET_SHAREABLE_USERS_LIST, {
        onSuccess: ({ data, errors: e }) => {
          if (!e) {
            return (data.getShareableUsers || []).map(row => ({
              ...row,
              fullName: `${row.firstName} ${row.lastName}`,
            }));
          }
          return [];
        },
        signal,
      }),
    options: { enabled },
  } as any);
};

const GET_SHAREABLE_USERS_LIST = gql`
  query getShareableUsers {
    getShareableUsers {
      id
      firstName
      lastName
    }
  }
`;

export const RERUN_DASHBOARD = gql`
  mutation processDashboardReport($id: String!) {
    processDashboardReport(id: $id) {
      workflowRun {
        id
        runId
      }
    }
  }
`;
export const DUPLICATE_DASHBOARD = gql`
  mutation duplicateDashboardReport($id: String!) {
    duplicateDashboardReport(id: $id) {
      id
      name
    }
  }
`;

export const CREATE_DASHBOARD = gql`
  mutation createUserDashboardReport($dashboardReportDto: DashboardReportInput!) {
    createUserDashboardReport(dashboardReportDto: $dashboardReportDto) {
      id
    }
  }
`;
export const UPDATE_DASHBOARD = gql`
  mutation updateUserDashboardReport($dashboardReportDto: DashboardReportInput!) {
    updateUserDashboardReport(dashboardReportDto: $dashboardReportDto) {
      id
    }
  }
`;

export const UPDATE_DASHBOARD_SHARED_USERS = gql`
  mutation updateUserDashboardReportSharedUsers($sharedUsers: DashboardReportPermissionInput!, $id: String!) {
    updateUserDashboardReportSharedUsers(sharedUsers: $sharedUsers, id: $id) {
      id
      dashboardReportPermission {
        type
        editors
        viewers
        permissionSets {
          permissionSetId
          sharedType
        }
      }
    }
  }
`;

const GET_CUSTOM_DASHBOARDS = gql`
  query findDashboardReports($type: String!, $excludePredefined: Boolean) {
    findDashboardReports(type: $type, excludePredefined: $excludePredefined) {
      id
      name
      description
      tags
      type
      accountId
      active
      intervalExpression
      deliveryConfig
      lastRunAt
      apps
      createdByUserId
      updatedByUserId
      updatedAt
      predefined
      globalFilters
      dashboardReportPermission {
        type
        editors
        viewers
        permissionSets {
          permissionSetId
          sharedType
        }
      }
      widgets {
        id
        type
        category
        drillDownHierarchy {
          fields {
            name
            alias
          }
        }
        requests {
          filter
          groupBy
          distinct
          source
          metricsFilterThreshold
          select {
            metrics {
              name
              alias
            }
            dims {
              name
              alias
              expression
            }
          }
          sorting {
            name
            dir
            path {
              path {
                name
              }
            }
          }
          top {
            size
            groupOthers
            offset
          }
          projectionId {
            name
            builtIn
          }
          timeRange
        }
        definition {
          title
          coords
          custom
        }
      }
      userPermissionType
    }
  }
`;

export const DELETE_DASHBOARDS = gql`
  mutation deleteUserDashboardReport($ids: [String]!) {
    deleteUserDashboardReport(ids: $ids)
  }
`;

export const EXPORT_PAGE = gql`
  query triggerDashboardExport($dashboardExportDto: TriggerDashboardExportInput!) {
    triggerDashboardExport(dashboardExportDto: $dashboardExportDto) {
      runId
      wfId
    }
  }
`;

export const ACTIVATE_REPORTS = gql`
  mutation setDashboardReportStatus($ids: [String!]!, $active: Boolean!) {
    setDashboardReportStatus(ids: $ids, active: $active)
  }
`;

export const ADD_APP_REPORTS = gql`
  mutation addDashboardReportApp($ids: [String!]!, $app: String!) {
    addDashboardReportApp(ids: $ids, app: $app)
  }
`;

const EDIT_REPORT_SCHEDULE = gql`
  mutation updateUserDashboardReportSchedule($dashboardScheduleDto: DashboardScheduleDto!) {
    updateUserDashboardReportSchedule(dashboardScheduleDto: $dashboardScheduleDto) {
      id
    }
  }
`;
